'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { DownloadIcon, Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { type ReactNode, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import AddPasskeyDialog from '@/components/dialog/settings/add-passkey-dialog'
import ChangePasswordCard from '@/components/dialog/settings/change-password-card'
import DeletePasskeyDialog from '@/components/dialog/settings/delete-passkey-dialog'
import {
  SettingsCard,
  SettingsCardHeader,
  SettingsRow,
  SettingsRowGroup,
} from '@/components/dialog/settings/settings-card'
import {
  SETTINGS_ITEM_ATTR,
  SETTINGS_LIST_CLASS,
  SETTINGS_LIST_ITEM_CLASS,
  SETTINGS_PANEL_CLASS,
} from '@/components/dialog/settings/settings-card-styles'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth/hooks'
import use2Fa from '@/features/auth/hooks/use-2fa'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import {
  useDisable2FA,
  useGet2FABackupCodes,
  useGetUserPasskeyList,
  userProfileKeys,
  useSetup2FA,
} from '@/service/user-profile.hook'
import { PasskeyInfo } from '@/types/generated/common/auth'

export default function SettingSecurity() {
  const t = useTranslations('settings')
  const router = useRouter()
  const { setTwoFactorSetup } = use2Fa()
  const { hasPassword } = useAuth()
  const {
    data: passkeyList,
    isLoading: isPasskeyListLoading,
    refetch: refetchPasskeyList,
  } = useGetUserPasskeyList()
  const { data: backupCodes, isLoading: isBackupCodesLoading } = useGet2FABackupCodes(
    {},
    { treatMissingAsDisabled: true }
  )
  const pathname = usePathname()
  const is2FAStatusLoading = isBackupCodesLoading && !backupCodes
  const isPasskeyStatusLoading = isPasskeyListLoading && !passkeyList
  const backupCodesLength = backupCodes?.backupCodes.length ?? 0
  const { mutateAsync: setup2fa, isPending: isSettingUp2FA } = useSetup2FA({
    onSuccess: (data) => {
      if (data.success) {
        setTwoFactorSetup({ secret: data.secret ?? '', backupCodes: data.backupCodes ?? [] })
        router.push(`/mfa/setup?callbackUrl=${encodeURIComponent(pathname)}`)
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleOnClickSetup2FA = async () => {
    await setup2fa({})
  }

  const handleOnClickBackupCodes = async () => {
    if (!backupCodes?.backupCodes.length) return
    const blob = new Blob([backupCodes.backupCodes.join('\n') ?? ''], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orderx-chat-2fa-backup-codes.txt`
    a.click()
  }

  return (
    <div className={SETTINGS_PANEL_CLASS}>
      <ProfileCard />
      {hasPassword && <ChangePasswordCard is2faEnabled={backupCodesLength > 0} />}
      <SettingsCard layout="row">
        <SettingsCardHeader
          title={t('twoFactorAuthentication')}
          description={t('twoFactorDescription')}
        />
        {is2FAStatusLoading ? (
          <SettingActionSkeleton />
        ) : backupCodesLength > 0 ? (
          <Disable2FAButton />
        ) : (
          <Button
            variant="secondary"
            size="sm"
            disabled={isSettingUp2FA}
            onClick={handleOnClickSetup2FA}
          >
            {isSettingUp2FA ? <Loader2Icon className="size-3.5 animate-spin" /> : t('enable')}
          </Button>
        )}
      </SettingsCard>
      <SettingsCard>
        <div className="flex w-full items-center justify-between gap-4">
          <SettingsCardHeader title={t('passkeys')} description={t('passkeysDescription')} />
          <AddPasskeyDialog refetchPasskeyList={refetchPasskeyList} />
        </div>
        {isPasskeyStatusLoading ? (
          <PasskeyListSkeleton />
        ) : (
          <div className={SETTINGS_LIST_CLASS}>
            {passkeyList?.passkeys?.map((item) => (
              <RegisteredPasskeyCard key={item.credentialId} passkey={item} />
            ))}
          </div>
        )}
      </SettingsCard>
      {backupCodesLength > 0 && (
        <SettingsCard layout="row">
          <SettingsCardHeader
            title={t('downloadBackupCodes')}
            description={t('downloadBackupCodesDescription')}
          />
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('downloadBackupCodes')}
            onClick={handleOnClickBackupCodes}
          >
            <DownloadIcon className="size-3.5" />
          </Button>
        </SettingsCard>
      )}
    </div>
  )
}

/** Read-only summary of the signed-in OrderX login (not a venue account — those live
 * under Settings → Accounts). */
const ProfileCard = () => {
  const t = useTranslations('settings')
  const { user } = useAuth()
  if (!user) return null

  const createdAtMs = Number(user.createdAt)
  const signInMethod = user.googleId
    ? user.hasPassword
      ? t('signInGoogleAndPassword')
      : t('signInGoogle')
    : t('signInPassword')

  return (
    <SettingsCard>
      <SettingsCardHeader title={t('profile')} description={t('profileDescription')} />
      {/* Label/value SettingsRows, like General — read-only, so the "control" is the value. */}
      <SettingsRowGroup>
        <SettingsRow
          label={t('email')}
          control={
            <ProfileValue>
              <span className="truncate" title={user.email}>
                {user.email}
              </span>
              <span
                className={cn(
                  'shrink-0 text-xs',
                  user.emailVerified ? 'text-chart-mint' : 'text-foreground/40'
                )}
              >
                {user.emailVerified ? t('emailVerified') : t('emailNotVerified')}
              </span>
            </ProfileValue>
          }
        />
        <SettingsRow
          label={t('signInMethod')}
          control={<ProfileValue>{signInMethod}</ProfileValue>}
        />
        {createdAtMs > 0 && (
          <SettingsRow
            label={t('joined')}
            control={<ProfileValue>{format(createdAtMs, 'MMM d, yyyy')}</ProfileValue>}
          />
        )}
      </SettingsRowGroup>
    </SettingsCard>
  )
}

const ProfileValue = ({ children }: { children: ReactNode }) => (
  <span className="text-foreground/80 flex max-w-64 min-w-0 items-center gap-2 text-sm tracking-tight">
    {children}
  </span>
)

const SettingActionSkeleton = () => <Skeleton className="bg-foreground/10 h-8 w-24 rounded-full" />

const PasskeyListSkeleton = () => (
  <div className={SETTINGS_LIST_CLASS}>
    {Array.from({ length: 2 }).map((_, index) => (
      <div key={index} className={SETTINGS_LIST_ITEM_CLASS}>
        <div className="flex flex-col gap-2">
          <Skeleton className="bg-foreground/10 h-4 w-36" />
          <Skeleton className="bg-foreground/10 h-3 w-28" />
        </div>
        <Skeleton className="bg-foreground/10 size-8 rounded-full" />
      </div>
    ))}
  </div>
)

const RegisteredPasskeyCard = ({ passkey }: { passkey: PasskeyInfo }) => {
  const t = useTranslations('settings')

  return (
    <div key={passkey.credentialId} {...SETTINGS_ITEM_ATTR} className={SETTINGS_LIST_ITEM_CLASS}>
      <div className="flex flex-col gap-2">
        <p className="text-foreground text-sm tracking-tight">{passkey.credentialName}</p>
        <time className="text-foreground/60 text-xs tracking-tight">
          {t('addedOn', { date: format(Number(passkey.createdAt), 'MMM dd, yyyy') })}
        </time>
      </div>
      <DeletePasskeyDialog passkey={passkey} />
    </div>
  )
}

type FormData = {
  currentPassword?: string
  backupCode: string
}

const Disable2FAButton = () => {
  const t = useTranslations('settings')
  const { hasPassword, user } = useAuth()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const schema = useMemo(
    () =>
      z.object({
        currentPassword: z
          .string()
          .min(1, {
            message: t('currentPasswordRequired'),
          })
          .optional(),
        backupCode: z.string().length(8, {
          message: t('backupCodeRequired'),
        }),
      }),
    [t]
  )
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  })
  const { setTwoFactorSetup } = use2Fa()
  const { data: backupCodes } = useGet2FABackupCodes({}, { treatMissingAsDisabled: true })
  const { mutateAsync: disable2fa } = useDisable2FA({
    onSuccess: (data) => {
      if (data.success) {
        setTwoFactorSetup(null)
        queryClient.invalidateQueries({ queryKey: userProfileKeys.backupCodes(user?.userId) })
        toast.success(t('twoFactorDisabledSuccess'))
        form.reset()
        setIsOpen(false)
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = async (data: FormData) => {
    await disable2fa({
      currentPassword: data.currentPassword ?? undefined,
      backupCode: data.backupCode,
    })
  }

  const handleOnClickBackupCodes = async () => {
    if (!backupCodes) return
    const blob = new Blob([backupCodes.backupCodes.join('\n') ?? ''], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orderx-chat-2fa-backup-codes.txt`
    a.click()
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          {t('disable2fa')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="flex flex-col gap-2">
          {t('disableTwoFactorAuthentication')}
          <p className="text-foreground/60 text-sm font-normal tracking-tight">
            {t('disable2faDescription')}
          </p>
        </DialogTitle>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-4 flex w-full flex-col gap-4"
          data-1p-ignore={false}
        >
          {hasPassword && (
            <div className="flex flex-col gap-1">
              <Label className="text-sm tracking-tight">{t('currentPassword')}</Label>
              <Input
                type="password"
                placeholder={t('currentPassword')}
                {...form.register('currentPassword')}
              />
            </div>
          )}
          <div className="flex w-full flex-col gap-1">
            <Label className="text-sm tracking-tight">{t('backupCode')}</Label>
            <InputOTP
              maxLength={8}
              inputMode="text"
              pattern="^[a-zA-Z0-9]*$"
              autoComplete="one-time-code"
              aria-label={t('backupCode')}
              pasteTransformer={(value) => value.replace(/-/g, '').trim()}
              value={form.watch('backupCode') ?? ''}
              onChange={(value) => {
                form.setValue('backupCode', value.replace(/-/g, ''), { shouldValidate: true })
              }}
            >
              <div className="mx-auto flex items-center gap-2">
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot className="w-8" index={0} />
                  <InputOTPSlot className="w-8 border-l" index={1} />
                  <InputOTPSlot className="w-8 border-l" index={2} />
                  <InputOTPSlot className="w-8 border-l" index={3} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot className="w-8" index={4} />
                  <InputOTPSlot className="w-8 border-l" index={5} />
                  <InputOTPSlot className="w-8 border-l" index={6} />
                  <InputOTPSlot className="w-8 border-l" index={7} />
                </InputOTPGroup>
              </div>
            </InputOTP>
            {form.formState.errors.backupCode?.message && (
              <p className="text-destructive text-sm" role="alert">
                {form.formState.errors.backupCode.message}
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={
              form.formState.isSubmitting ||
              form.watch('backupCode')?.length !== 8 ||
              (hasPassword && !form.watch('currentPassword'))
            }
          >
            {t('verify')}
          </Button>
        </form>
        <div className="text-foreground/60 mx-auto text-sm tracking-tight">
          {t('lostBackupCodes')}
          <button
            className="text-primary ml-3 cursor-pointer font-medium"
            onClick={handleOnClickBackupCodes}
          >
            {t('download')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
